import React, { useState, useEffect, useMemo } from "react";
import { useTranslationReload } from "../hooks/useTranslationReload";
import { getAllEquipment } from "../backend/api/livepal";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CustomCheckbox } from "./CustomCheckbox";

interface SystemSelectProps {
  value: number[]; // array di id selezionati
  onChange: (value: number[]) => void;
  useTranslationKeys?: boolean;
}

import { IoChevronDown } from "react-icons/io5";

const SystemSelect: React.FC<SystemSelectProps> = ({
  value,
  onChange,
  useTranslationKeys = false,
}) => {
  const { t } = useTranslationReload();
  const { i18n } = useTranslation();
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  // Fetch equipment dal backend
  const { data: backendSystems, isLoading } = useQuery({
    queryKey: ["equipment", i18n.language],
    queryFn: () => getAllEquipment(i18n.language),
  });

  // Mappa i dati backend in oggetto {category: {subCategory: items[]}}
  const systemsToUse = useMemo(() => {
    if (!backendSystems) return {};
    const result: any = {};
    backendSystems.forEach((eq: any) => {
      if (!result[eq.category]) result[eq.category] = {};
      result[eq.category][eq.subCategory] = eq.items; // items: [{id, label}]
    });
    return result;
  }, [backendSystems]);

  const isItemSelected = (item: { id: number }) => value.includes(item.id);

  const handleItemToggle = (item: { id: number }) => {
    let updatedIds: number[];
    if (value.includes(item.id)) {
      updatedIds = value.filter((id) => id !== item.id);
    } else {
      updatedIds = [...value, item.id];
    }
    onChange(updatedIds);
  };

  const toggleCategory = (category: string) => {
    setOpenCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {Object.entries(systemsToUse).map(([category, subCategories]) => {
        const isOpen = openCategories.includes(category);
        const selectedCount = Object.values(subCategories as any)
          .flatMap((items: any) => items)
          .filter((item: any) => value.includes(item.id)).length;

        return (
          <div
            key={category}
            className={`border rounded-2xl transition-all duration-300 overflow-hidden ${
              isOpen
                ? "bg-[#331855]/40 border-secondary/30 shadow-[0_0_25px_rgba(255,124,103,0.05)]"
                : "bg-black/20 border-white/5 hover:border-white/10"
            }`}
          >
            {/* Header / Trigger */}
            <button
              type="button"
              onClick={() => toggleCategory(category)}
              className="w-full px-5 py-4 flex items-center justify-between group transition-colors"
            >
              <div className="flex items-center gap-4">
                <span
                  className={`text-sm font-bold uppercase tracking-widest transition-colors ${
                    isOpen
                      ? "text-secondary"
                      : "text-white/60 group-hover:text-white/90"
                  }`}
                >
                  {category}
                </span>
                {selectedCount > 0 && (
                  <span className="bg-secondary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-secondary/20 shadow-lg animate-scale-in">
                    {selectedCount}
                  </span>
                )}
              </div>
              <IoChevronDown
                size={20}
                className={`transition-transform duration-300 ${
                  isOpen ? "rotate-180 text-secondary" : "text-white/30"
                }`}
              />
            </button>

            {/* Content Body */}
            <div
              className={`transition-all duration-500 ease-in-out ${
                isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
              } overflow-hidden`}
            >
              <div className="px-5 pb-6 pt-2 space-y-6">
                {Object.entries(subCategories as any).map(
                  ([subCategory, items]) => (
                    <div key={subCategory} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="h-[1px] w-4 bg-secondary/30" />
                        <h4 className="text-[12px] font-bold text-white uppercase tracking-wider">
                          {subCategory}
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 pl-2">
                        {(items as { id: number; label: string }[]).map(
                          (item) => (
                            <CustomCheckbox
                              key={item.id}
                              checked={isItemSelected(item)}
                              onChange={() => handleItemToggle(item)}
                              label={item.label}
                            />
                          )
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SystemSelect;
